import React, { useState, useEffect, useRef, useCallback } from 'react';
import ReactDOM from 'react-dom';
import { X, ChevronLeft, ChevronRight, Trash2, Sparkles, Share2, RotateCw, Info, Ellipsis } from 'lucide-react';
import { Photo } from '../types';
import { doc, updateDoc, onSnapshot } from 'firebase/firestore';
import { db } from '../firebase';
import { Timestamp } from 'firebase/firestore';
import { TransformWrapper, TransformComponent, useControls } from 'react-zoom-pan-pinch';
import AiLensOverlay from './AiLensOverlay';
import { ActionSheet } from './ui/ActionSheet';
import { triggerLensAnalysis } from '../services/photoService';

class ImageCache {
  private cache = new Map<string, HTMLImageElement>();
  private loadingPromises = new Map<string, Promise<HTMLImageElement>>();

  preloadImage(url: string): Promise<HTMLImageElement> {
    if (this.cache.has(url)) return Promise.resolve(this.cache.get(url)!);
    if (this.loadingPromises.has(url)) return this.loadingPromises.get(url)!;

    const loadPromise = new Promise<HTMLImageElement>((resolve, reject) => {
      const img = new Image();
      img.onload = () => { this.cache.set(url, img); this.loadingPromises.delete(url); resolve(img); };
      img.onerror = () => { this.loadingPromises.delete(url); reject(new Error(`Failed: ${url}`)); };
      img.src = url;
    });

    this.loadingPromises.set(url, loadPromise);
    return loadPromise;
  }

  isImageCached(url: string): boolean { return this.cache.has(url); }
  clear() { this.cache.clear(); this.loadingPromises.clear(); }
  getCacheStats() { return { cached: this.cache.size, loading: this.loadingPromises.size }; }

  preloadBatch(urls: string[], priority: string[] = []) {
    priority.forEach(url => {
      if (!this.cache.has(url) && !this.loadingPromises.has(url)) {
        this.preloadImage(url).catch(() => {});
      }
    });
    setTimeout(() => {
      urls.forEach(url => {
        if (!this.cache.has(url) && !this.loadingPromises.has(url)) {
          this.preloadImage(url).catch(() => {});
        }
      });
    }, 100);
  }
}

const imageCache = new ImageCache();

interface ExtendedPhoto extends Photo {
  rotation?: number;
}

interface PhotoViewerProps {
  photos: ExtendedPhoto[];
  initialPhotoIndex?: number;
  onClose: () => void;
  onDelete?: (photoId: string) => void;
  contextLabel?: string;
}

const ZoomControls: React.FC<{ showUI: boolean; scale: number }> = ({ showUI, scale }) => {
  const { zoomIn, zoomOut, resetTransform } = useControls();

  if (!showUI || scale <= 1.01) return null;

  return (
    <div className="absolute bottom-32 left-1/2 transform -translate-x-1/2 z-30 flex items-center space-x-2 bg-black/60 backdrop-blur-sm rounded-full px-4 py-2 transition-opacity duration-300">
      <button
        onClick={() => zoomOut()}
        className="w-10 h-10 flex items-center justify-center text-white hover:bg-white/20 rounded-full transition-colors text-2xl font-light"
      >
        -
      </button>
      <button
        onClick={() => resetTransform()}
        className="w-12 h-10 flex items-center justify-center text-white hover:bg-white/20 rounded-full transition-colors text-sm font-medium"
      >
        1:1
      </button>
      <button
        onClick={() => zoomIn()}
        className="w-10 h-10 flex items-center justify-center text-white hover:bg-white/20 rounded-full transition-colors text-xl font-light"
      >
        +
      </button>
    </div>
  );
};

export const PhotoViewer: React.FC<PhotoViewerProps> = ({
  photos,
  initialPhotoIndex = 0,
  onClose,
  onDelete,
  contextLabel,
}) => {
  const [currentPhotoIndex, setCurrentPhotoIndex] = useState(initialPhotoIndex);
  const [currentPhoto, setCurrentPhoto] = useState<ExtendedPhoto>(photos[initialPhotoIndex]);
  const [loading, setLoading] = useState(true);
  const [imageLoaded, setImageLoaded] = useState(false);
  const [imageError, setImageError] = useState(false);
  const [imageDimensions, setImageDimensions] = useState({ width: 0, height: 0 });

  const [showUI, setShowUI] = useState(true);
  const [showActionSheet, setShowActionSheet] = useState(false);
  const [showInfo, setShowInfo] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [showLensOverlay, setShowLensOverlay] = useState(false);
  const [liveAiLens, setLiveAiLens] = useState(currentPhoto.aiLens);

  const [rotation, setRotation] = useState(currentPhoto.rotation || 0);
  const [currentScale, setCurrentScale] = useState(1);
  const [slideDirection, setSlideDirection] = useState<'none' | 'left' | 'right'>('none');

  // Panning/swipe state for gesture detection via TransformWrapper callbacks
  const panStartRef = useRef<{ x: number; y: number; time: number; scale: number } | null>(null);
  const clickTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const thumbnailsRef = useRef<HTMLDivElement>(null);
  const currentProjectIdRef = useRef<string>('');

  const preloadAdjacentImages = useCallback((centerIndex: number) => {
    const priorityUrls: string[] = [];
    const allUrls: string[] = [];

    if (photos[centerIndex]) priorityUrls.push(photos[centerIndex].url);
    for (let offset = 1; offset <= 2; offset++) {
      if (centerIndex - offset >= 0) {
        const url = photos[centerIndex - offset].url;
        allUrls.push(url);
        if (offset === 1) priorityUrls.push(url);
      }
      if (centerIndex + offset < photos.length) {
        const url = photos[centerIndex + offset].url;
        allUrls.push(url);
        if (offset === 1) priorityUrls.push(url);
      }
    }

    imageCache.preloadBatch(allUrls, priorityUrls);
  }, [photos]);

  useEffect(() => {
    const projectId = photos[0]?.projectId || '';
    if (currentProjectIdRef.current !== projectId) {
      imageCache.clear();
      currentProjectIdRef.current = projectId;
    }
    preloadAdjacentImages(initialPhotoIndex);
  }, [photos, initialPhotoIndex, preloadAdjacentImages]);

  useEffect(() => {
    const photo = photos[currentPhotoIndex];
    setCurrentPhoto(photo);
    setLiveAiLens(photo.aiLens);
    setShowLensOverlay(false);
    setLoading(true);
    setImageLoaded(false);
    setImageError(false);
    setRotation(photo.rotation || 0);
    setCurrentScale(1);
    preloadAdjacentImages(currentPhotoIndex);

    // Reset slide direction after the animation completes. 400ms, not 340 —
    // the animation is 340ms, so clearing on the same tick can land a
    // frame early and cut the ease-out short.
    const slideTimer = setTimeout(() => setSlideDirection('none'), 400);
    return () => clearTimeout(slideTimer);
  }, [currentPhotoIndex, photos, preloadAdjacentImages]);

  // Real-time Firestore listener for AI Lens updates on current photo
  useEffect(() => {
    if (!currentPhoto?.id) return;

    const photoRef = doc(db, 'photos', currentPhoto.id);
    const unsubscribe = onSnapshot(photoRef, (snapshot) => {
      const data = snapshot.data();
      if (data?.aiLens) {
        setLiveAiLens(data.aiLens);
      }
    }, (error) => {
      console.error('AI Lens listener error:', error);
    });

    return () => unsubscribe();
  }, [currentPhoto?.id]);

  // Keep the latest key-action handlers in a ref so the keydown listener (which
  // subscribes once) always calls current closures instead of stale ones from
  // the first render. Avoids both stale-closure bugs and re-subscribing on every
  // keystroke/navigation. (#16/#27)
  const keyActionsRef = useRef({
    onClose,
    goToPrevPhoto: () => {},
    goToNextPhoto: () => {},
    handleRotate: () => {},
    toggleUI: () => {},
  });

  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      const a = keyActionsRef.current;
      switch (e.key) {
        case 'Escape': a.onClose(); break;
        case 'ArrowLeft': a.goToPrevPhoto(); break;
        case 'ArrowRight': a.goToNextPhoto(); break;
        case 'r': case 'R': a.handleRotate(); break;
        case ' ': e.preventDefault(); a.toggleUI(); break;
      }
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, []);

  const toggleUI = () => {
    setShowUI(prev => !prev);
  };

  const goToNextPhoto = useCallback(() => {
    if (currentPhotoIndex < photos.length - 1) {
      setSlideDirection('left');
      setCurrentPhotoIndex(prev => prev + 1);
    }
  }, [currentPhotoIndex, photos.length]);

  const goToPrevPhoto = useCallback(() => {
    if (currentPhotoIndex > 0) {
      setSlideDirection('right');
      setCurrentPhotoIndex(prev => prev - 1);
    }
  }, [currentPhotoIndex]);

  const goToPhoto = (index: number) => {
    setCurrentPhotoIndex(index);
  };

  const handleRotate = async () => {
    const newRotation = (rotation + 90) % 360;
    setRotation(newRotation);
    const updatedPhoto = { ...currentPhoto, rotation: newRotation };
    setCurrentPhoto(updatedPhoto);
    try {
      const photoRef = doc(db, 'photos', currentPhoto.id);
      await updateDoc(photoRef, { rotation: newRotation });
    } catch (error) {
      console.error('Error updating photo rotation:', error);
    }
  };

  // Keep the keydown handler ref pointed at the current closures (see the
  // keydown useEffect above). Assigning during render is fine for a ref.
  keyActionsRef.current = { onClose, goToPrevPhoto, goToNextPhoto, handleRotate, toggleUI };

  const handleShare = async () => {
    if (navigator.share && currentPhoto?.url) {
      try { await navigator.share({ title: 'Foto från Stadsauktion', url: currentPhoto.url }); }
      catch { /* Share cancelled */ }
    } else if (currentPhoto?.url) {
      navigator.clipboard.writeText(currentPhoto.url);
      const toast = document.createElement('div');
      toast.className = 'fixed top-4 right-4 bg-black bg-opacity-75 text-white px-4 py-2 rounded-lg z-50';
      toast.textContent = 'Länk kopierad!';
      document.body.appendChild(toast);
      setTimeout(() => document.body.removeChild(toast), 2000);
    }
  };

  const handleTriggerAnalysis = async () => {
    if (!currentPhoto?.url || !currentPhoto?.id) return;
    try {
      await triggerLensAnalysis(
        currentPhoto.id,
        currentPhoto.url,
        currentPhoto.projectId,
        currentPhoto.roomId
      );
    } catch (error) {
      console.error('Error triggering AI Lens analysis:', error);
    }
    setShowLensOverlay(false);
  };

  const handleAiLensAction = () => {
    if (liveAiLens?.status === 'completed') {
      setShowLensOverlay(true);
    } else if (liveAiLens?.status !== 'analyzing') {
      handleTriggerAnalysis();
    }
  };

  const handleDelete = () => { setShowDeleteConfirm(true); };
  const confirmDelete = () => {
    if (onDelete && currentPhoto) {
      onDelete(currentPhoto.id);
      setShowDeleteConfirm(false);
      if (photos.length <= 1) {
        onClose();
      } else if (currentPhotoIndex >= photos.length - 1) {
        setCurrentPhotoIndex(prev => Math.max(0, prev - 1));
      }
    }
  };

  const handleImageLoad = (e: React.SyntheticEvent<HTMLImageElement>) => {
    const img = e.target as HTMLImageElement;
    setImageDimensions({ width: img.naturalWidth, height: img.naturalHeight });
    setImageLoaded(true);
    setLoading(false);
  };

  // TransformWrapper panning callbacks for swipe detection
  const handlePanningStart = useCallback((_ref: any, event: TouchEvent | MouseEvent) => {
    if (_ref.state.scale > 1.05) {
      panStartRef.current = null;
      return;
    }
    const point = 'touches' in event ? event.touches[0] : event;
    panStartRef.current = {
      x: point.clientX,
      y: point.clientY,
      time: Date.now(),
      scale: _ref.state.scale,
    };
  }, []);

  const handlePanningStop = useCallback((_ref: any, event: TouchEvent | MouseEvent) => {
    if (!panStartRef.current || panStartRef.current.scale > 1.05) return;

    const point = 'changedTouches' in event ? (event as TouchEvent).changedTouches[0] : (event as MouseEvent);
    const deltaX = point.clientX - panStartRef.current.x;
    const deltaY = point.clientY - panStartRef.current.y;
    const elapsed = Date.now() - panStartRef.current.time;
    const absDeltaX = Math.abs(deltaX);
    const absDeltaY = Math.abs(deltaY);

    panStartRef.current = null;

    // Horizontal swipe: mostly horizontal, significant distance
    if (absDeltaX > 40 && absDeltaX > absDeltaY * 1.5 && elapsed < 600) {
      if (deltaX > 0) goToPrevPhoto();
      else goToNextPhoto();
      return;
    }

    // Vertical swipe down to close (drag-to-dismiss)
    if (deltaY > 80 && absDeltaY > absDeltaX * 1.5 && elapsed < 500) {
      onClose();
      return;
    }
  }, [goToPrevPhoto, goToNextPhoto, onClose]);

  // Single tap to toggle UI, with delay to avoid firing on double-tap (zoom)
  const handleContainerClick = useCallback((e: React.MouseEvent) => {
    if ((e.target as HTMLElement).closest('button')) return;

    if (clickTimerRef.current) {
      clearTimeout(clickTimerRef.current);
      clickTimerRef.current = null;
      return;
    }

    clickTimerRef.current = setTimeout(() => {
      clickTimerRef.current = null;
      toggleUI();
    }, 300);
  }, []);

  // Date formatting helpers for the header pill
  const toDate = (timestamp: Timestamp | string): Date | null => {
    if (timestamp && typeof timestamp === 'object' && 'toDate' in timestamp) {
      return (timestamp as Timestamp).toDate();
    } else if (typeof timestamp === 'string') {
      return new Date(timestamp);
    }
    return null;
  };

  const formatDateShort = (timestamp: Timestamp | string) => {
    const date = toDate(timestamp);
    if (!date) return 'Okänt datum';
    return date.toLocaleDateString('sv-SE', { day: 'numeric', month: 'long' });
  };

  const formatTimeShort = (timestamp: Timestamp | string) => {
    const date = toDate(timestamp);
    if (!date) return '';
    return date.toLocaleTimeString('sv-SE', { hour: '2-digit', minute: '2-digit' });
  };

  const formatDate = (timestamp: Timestamp | string) => {
    const date = toDate(timestamp);
    if (!date) return 'Okänt datum';
    return date.toLocaleDateString('sv-SE', {
      year: 'numeric', month: 'long', day: 'numeric', hour: '2-digit', minute: '2-digit'
    });
  };

  const scrollThumbnailToCenter = (index: number) => {
    if (thumbnailsRef.current) {
      const thumbnail = thumbnailsRef.current.children[index] as HTMLElement;
      if (thumbnail) thumbnail.scrollIntoView({ behavior: 'smooth', inline: 'center' });
    }
  };

  useEffect(() => {
    scrollThumbnailToCenter(currentPhotoIndex);
  }, [currentPhotoIndex]);

  const isCurrentImageCached = imageCache.isImageCached(currentPhoto.url);

  // ActionSheet actions for ellipsis menu
  const ellipsisActions = [
    {
      icon: <Share2 size={20} />,
      label: 'Dela',
      onClick: handleShare,
    },
    {
      icon: <RotateCw size={20} />,
      label: 'Rotera',
      onClick: handleRotate,
    },
    ...(liveAiLens?.status === 'completed' ? [{
      icon: <Sparkles size={20} />,
      label: 'AI Lens',
      subtitle: 'Visa analys',
      onClick: () => setShowLensOverlay(true),
    }] : [{
      icon: <Sparkles size={20} />,
      label: 'Analysera med AI',
      subtitle: liveAiLens?.status === 'analyzing' ? 'Analyserar...' : undefined,
      onClick: handleTriggerAnalysis,
      disabled: liveAiLens?.status === 'analyzing',
    }]),
    {
      icon: <Info size={20} />,
      label: 'Information',
      onClick: () => setShowInfo(true),
    },
    ...(onDelete ? [{
      icon: <Trash2 size={20} />,
      label: 'Ta bort',
      onClick: handleDelete,
      variant: 'danger' as const,
    }] : []),
  ];

  const viewerContent = (
    <div className="fixed inset-0 lg:left-[240px] bg-black" style={{ zIndex: 99999 }}>
      {/* Photo display - key forces remount on photo change, resetting zoom/pan */}
      <div
        className="w-full h-full flex items-center justify-center"
        onClick={handleContainerClick}
      >
        {/* The animation lives on this inner element, keyed the same as the
            TransformWrapper below. Previously it sat on the outer container
            while only the inner subtree remounted, so the new photo appeared
            instantly and the animation restarted mid-flight — it read as a
            snap. Keying the animated element itself means each photo plays its
            ease-in from the start. */}
        <div
          key={`slide-${currentPhotoIndex}`}
          className={`w-full h-full flex items-center justify-center ${
            slideDirection === 'left' ? 'animate-photo-slide-left' :
            slideDirection === 'right' ? 'animate-photo-slide-right' :
            'animate-fade-in'
          }`}
        >
        <TransformWrapper
          key={currentPhotoIndex}
          initialScale={1}
          minScale={1}
          maxScale={5}
          wheel={{ step: 0.1 }}
          pinch={{ step: 5 }}
          doubleClick={{ disabled: false, mode: 'toggle', animationTime: 200 }}
          // At scale 1 the image fills the frame and there is nothing to pan
          // to, so free panning only let a horizontal swipe drag the photo up
          // and down. Lock the axis: unzoomed = horizontal only (that is the
          // flip gesture), zoomed = free panning in both axes as normal.
          panning={{
            disabled: false,
            velocityDisabled: false,
            lockAxisY: currentScale <= 1.05,
          }}
          centerOnInit={true}
          limitToBounds={true}
          centerZoomedOut={true}
          onPanningStart={handlePanningStart}
          onPanningStop={handlePanningStop}
          onTransformed={(ref) => {
            setCurrentScale(ref.state.scale);
          }}
        >
          {() => (
            <>
              <TransformComponent
                wrapperStyle={{ width: '100%', height: '100%' }}
                contentStyle={{ width: '100%', height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center' }}
              >
                {imageError ? (
                  <div className="flex flex-col items-center justify-center gap-3 text-white/70 w-[200px] h-[200px]">
                    <svg xmlns="http://www.w3.org/2000/svg" width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"><rect x="3" y="3" width="18" height="18" rx="2"/><circle cx="8.5" cy="8.5" r="1.5"/><path d="m21 15-5-5L5 21"/></svg>
                    <p className="text-sm">Bilden kunde inte laddas</p>
                  </div>
                ) : (
                  <img
                    src={currentPhoto.url}
                    alt={`Photo ${currentPhotoIndex + 1}`}
                    onLoad={handleImageLoad}
                    onError={() => { setImageError(true); setLoading(false); }}
                    className="max-w-full max-h-screen object-contain select-none pointer-events-none transition-transform duration-300"
                    style={{
                      transform: `rotate(${rotation}deg)`,
                      opacity: imageLoaded ? 1 : 0,
                    }}
                    draggable={false}
                  />
                )}
              </TransformComponent>
              <ZoomControls showUI={showUI} scale={currentScale} />
            </>
          )}
        </TransformWrapper>
        </div>
      </div>


      {/* iOS Photos-style header */}
      <div
        className={`absolute top-0 left-0 right-0 transition-opacity duration-300 ${showUI ? 'opacity-100' : 'opacity-0 pointer-events-none'}`}
        style={{ zIndex: 50, paddingTop: 'env(safe-area-inset-top, 0px)' }}
      >
        <div className="flex items-center justify-between px-3 py-2 bg-gradient-to-b from-black/50 via-black/25 to-transparent">
          {/* Back button */}
          <button
            onClick={onClose}
            aria-label="Tillbaka"
            className="flex items-center gap-0.5 text-white active:opacity-60 transition-opacity min-w-[44px] h-11"
          >
            <ChevronLeft size={28} strokeWidth={2.5} />
          </button>

          {/* Center pill */}
          <button
            className="flex flex-col items-center px-4 py-1.5 rounded-full bg-white/10 backdrop-blur-md"
            onClick={() => {/* Could navigate to album/project */}}
          >
            {contextLabel ? (
              <>
                <span className="text-[13px] font-semibold text-white leading-tight">{contextLabel}</span>
                <span className="text-[12px] text-white/60 leading-tight">
                  {formatDateShort(currentPhoto.takenAt)}  {formatTimeShort(currentPhoto.takenAt)}
                </span>
              </>
            ) : (
              <>
                <span className="text-[13px] font-semibold text-white leading-tight">
                  {formatDateShort(currentPhoto.takenAt)}
                </span>
                <span className="text-[12px] text-white/60 leading-tight">
                  {formatTimeShort(currentPhoto.takenAt)}
                </span>
              </>
            )}
          </button>

          {/* Ellipsis menu button */}
          <button
            onClick={() => setShowActionSheet(true)}
            aria-label="Fler alternativ"
            className="w-9 h-9 flex items-center justify-center text-white bg-white/10 backdrop-blur-md rounded-full active:bg-white/20 transition-colors"
          >
            <Ellipsis size={20} />
          </button>
        </div>
      </div>

      {/* Navigation arrows - desktop only (mobile uses swipe) */}
      {photos.length > 1 && (
        <>
          {currentPhotoIndex > 0 && (
            <button
              onClick={goToPrevPhoto}
              aria-label="Föregående foto"
              className={`hidden md:flex absolute left-2 top-1/2 -translate-y-1/2 w-11 h-11 items-center justify-center text-white bg-black/40 backdrop-blur-sm rounded-full hover:bg-black/60 transition-all ${showUI ? 'opacity-100' : 'opacity-0 pointer-events-none'}`}
              style={{ zIndex: 40 }}
            >
              <ChevronLeft size={22} />
            </button>
          )}
          {currentPhotoIndex < photos.length - 1 && (
            <button
              onClick={goToNextPhoto}
              aria-label="Nästa foto"
              className={`hidden md:flex absolute right-2 top-1/2 -translate-y-1/2 w-11 h-11 items-center justify-center text-white bg-black/40 backdrop-blur-sm rounded-full hover:bg-black/60 transition-all ${showUI ? 'opacity-100' : 'opacity-0 pointer-events-none'}`}
              style={{ zIndex: 40 }}
            >
              <ChevronRight size={22} />
            </button>
          )}
        </>
      )}

      {/* Thumbnail strip - always visible when UI shown */}
      {photos.length > 1 && (
        <div
          className={`absolute left-0 right-0 transition-opacity duration-300 ${showUI ? 'opacity-100' : 'opacity-0 pointer-events-none'}`}
          style={{ zIndex: 30, bottom: 'calc(56px + env(safe-area-inset-bottom, 0px))' }}
        >
          <div ref={thumbnailsRef} className="flex gap-[3px] overflow-x-auto no-scrollbar px-2 py-2">
            {photos.map((photo, index) => (
              <button
                key={photo.id}
                onClick={() => goToPhoto(index)}
                className={`flex-shrink-0 w-[44px] h-[44px] rounded-[3px] overflow-hidden transition-all duration-200 ${
                  index === currentPhotoIndex
                    ? 'ring-2 ring-white scale-[1.08]'
                    : 'opacity-50 active:opacity-80'
                }`}
              >
                <img src={photo.thumbnail || photo.url} alt="" className="w-full h-full object-cover" loading="lazy" />
              </button>
            ))}
          </div>
        </div>
      )}

      {/* Bottom action bar - iOS Photos style */}
      <div
        className={`absolute bottom-0 left-0 right-0 transition-opacity duration-300 ${showUI ? 'opacity-100' : 'opacity-0 pointer-events-none'}`}
        style={{ zIndex: 30, paddingBottom: 'env(safe-area-inset-bottom, 0px)' }}
      >
        <div className="bg-gradient-to-t from-black/60 via-black/30 to-transparent pt-6">
          <div className="flex items-center justify-around px-6 pb-2">
            <button onClick={handleShare} aria-label="Dela" className="w-11 h-11 flex items-center justify-center text-white active:bg-white/10 rounded-full transition-colors">
              <Share2 size={22} />
            </button>
            <button onClick={handleRotate} aria-label="Rotera" className="w-11 h-11 flex items-center justify-center text-white active:bg-white/10 rounded-full transition-colors">
              <RotateCw size={22} />
            </button>
            <button onClick={() => setShowInfo(true)} aria-label="Information" className="w-11 h-11 flex items-center justify-center text-white active:bg-white/10 rounded-full transition-colors">
              <Info size={22} />
            </button>
            <button onClick={handleAiLensAction} aria-label="AI Lens" className="w-11 h-11 flex items-center justify-center active:bg-white/10 rounded-full transition-colors">
              <Sparkles
                size={22}
                className={
                  liveAiLens?.status === 'analyzing' ? 'text-amber-300 animate-pulse' :
                  liveAiLens?.status === 'completed' ? 'text-violet-300' :
                  'text-white'
                }
              />
            </button>
            {onDelete && (
              <button onClick={handleDelete} aria-label="Ta bort" className="w-11 h-11 flex items-center justify-center text-white active:bg-white/10 rounded-full transition-colors">
                <Trash2 size={22} />
              </button>
            )}
          </div>
        </div>
      </div>

      {/* Photo info modal */}
      {showInfo && (
        <div className="absolute inset-0 flex items-end sm:items-center justify-center bg-black/50 backdrop-blur-sm" style={{ zIndex: 70 }}>
          <div className="bg-[var(--bg-surface)] rounded-t-2xl sm:rounded-2xl p-5 w-full sm:m-4 sm:max-w-sm"
            style={{ paddingBottom: 'max(20px, env(safe-area-inset-bottom))' }}
          >
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-base font-semibold text-[var(--text-primary)]">Fotoinformation</h3>
              <button onClick={() => setShowInfo(false)} aria-label="Stäng" className="text-[var(--text-tertiary)] p-1"><X size={20} /></button>
            </div>
            <div className="space-y-3 text-sm">
              <div className="flex justify-between">
                <span className="text-[var(--text-secondary)]">Datum</span>
                <span className="text-[var(--text-primary)]">{formatDate(currentPhoto.takenAt)}</span>
              </div>
              {currentPhoto.notes && (
                <div className="flex justify-between">
                  <span className="text-[var(--text-secondary)]">Anteckningar</span>
                  <span className="text-[var(--text-primary)] text-right max-w-[60%]">{currentPhoto.notes}</span>
                </div>
              )}
              {imageLoaded && (
                <div className="flex justify-between">
                  <span className="text-[var(--text-secondary)]">Storlek</span>
                  <span className="text-[var(--text-primary)]">{imageDimensions.width} × {imageDimensions.height}</span>
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Delete confirmation modal */}
      {showDeleteConfirm && (
        <div className="absolute inset-0 flex items-end sm:items-center justify-center bg-black/50 backdrop-blur-sm" style={{ zIndex: 70 }}>
          <div className="bg-[var(--bg-surface)] rounded-t-2xl sm:rounded-2xl p-5 w-full sm:m-4 sm:max-w-sm"
            style={{ paddingBottom: 'max(20px, env(safe-area-inset-bottom))' }}
          >
            <h3 className="text-base font-semibold text-[var(--text-primary)] mb-2">Ta bort foto?</h3>
            <p className="text-sm text-[var(--text-secondary)] mb-5">Är du säker? Detta går inte att ångra.</p>
            <div className="flex space-x-3">
              <button onClick={() => setShowDeleteConfirm(false)} className="flex-1 px-4 py-2.5 text-sm font-medium text-[var(--text-primary)] bg-[var(--bg-surface-alt)] rounded-xl transition-colors active:opacity-80">Avbryt</button>
              <button onClick={confirmDelete} className="flex-1 px-4 py-2.5 text-sm font-medium text-white bg-red-500 rounded-xl transition-colors active:bg-red-600">Ta bort</button>
            </div>
          </div>
        </div>
      )}

      {/* AI Lens Overlay */}
      {showLensOverlay && liveAiLens && liveAiLens.status === 'completed' && (
        <AiLensOverlay
          aiLens={liveAiLens}
          onClose={() => setShowLensOverlay(false)}
          onReanalyze={handleTriggerAnalysis}
        />
      )}

      {/* ActionSheet for ellipsis menu */}
      <ActionSheet
        isOpen={showActionSheet}
        onClose={() => setShowActionSheet(false)}
        actions={ellipsisActions}
      />

      {/* Loading indicator */}
      {loading && !isCurrentImageCached && (
        <div className="absolute inset-0 flex items-center justify-center pointer-events-none" style={{ zIndex: 20 }}>
          <div className="animate-spin rounded-full h-8 w-8 border-2 border-white/30 border-t-white"></div>
        </div>
      )}
    </div>
  );

  // Render via portal to escape any parent stacking contexts (PullToRefresh transforms)
  return ReactDOM.createPortal(viewerContent, document.body);
};

export default PhotoViewer;
